import React, { useEffect, useRef, useState } from "react";
import { Modal, Form, Input, Button, message, InputNumber, DatePicker, Spin, Empty, Card, Tooltip, Tabs, Segmented, Popconfirm } from "antd";
import { EditOutlined, SaveOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import dayjs from "dayjs";
import { MakeApiCall } from "../../../../apis";
import { Wrapper } from "../style";
import {
  buildCogsHistoryRequestBody,
  extractCogsHistoryRecords,
} from "../../../../utils/cogsHistoryApi";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Format date and time
const formatDateTime = (ts) =>
  new Date(ts * 1000).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

// Format just date for x-axis
const formatDateForXAxis = (ts) =>
  new Date(ts * 1000).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short'
  });

// Format with full date and time for tooltip
const formatTooltipDateTime = (ts) =>
  new Date(ts * 1000).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

const AddCogs = ({ visible, onClose, onSave, getList, product, userData, isUpdate, sku, marketplaceId, readOnly = false }) => {
  const isDark = document?.documentElement?.getAttribute("data-bs-theme") === "dark";

  // Get SP API connected date - it's a timestamp (seconds)
  const spApiCredentialsConnectedAtTimestamp = userData?.user?.user_data?.sp_api_credentials_connected_at;
  
  // Convert timestamp to dayjs object properly
  const getSpApiConnectedDate = () => {
    if (spApiCredentialsConnectedAtTimestamp && !isNaN(spApiCredentialsConnectedAtTimestamp)) {
      // Convert timestamp (seconds) to milliseconds and create dayjs object
      const date = dayjs.unix(spApiCredentialsConnectedAtTimestamp);
      if (date.isValid()) {
        return date;
      }
    }
    return null;
  };
  
  const spApiConnectedDate = getSpApiConnectedDate();
  
  // Calculate default start date safely
  const getDefaultStartDate = () => {
    if (spApiConnectedDate && spApiConnectedDate.isValid()) {
      return spApiConnectedDate.subtract(60, "day");
    }
    // If no valid SP API date, use current date minus 60 days
    return dayjs().subtract(60, "day");
  };
  
  const defaultStartDate = getDefaultStartDate();
  
  const [form] = Form.useForm();
  const [cogsHistory, setCogsHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeRightTab, setActiveRightTab] = useState("table");
  const chartRef = useRef(null);

  // NEW: State to check if start date is valid (not cleared)
  const [hasStartDate, setHasStartDate] = useState(true);
  
  // State for editable table
  const [editingRecord, setEditingRecord] = useState(null);
  const [editFormData, setEditFormData] = useState({ item_cogs: '', item_cogs_batch_start_date: null });
  const [updatingCogs, setUpdatingCogs] = useState(false);

  useEffect(() => {
    if (visible && product) {
  
      // If already has date use existing
      // otherwise first time default = SP API Connected Date - 60 days (or current - 60 days)
      let initialDate;
      if (product?.item_cogs_batch_start_date) {
        initialDate = dayjs(product.item_cogs_batch_start_date);
      } else {
        initialDate = getDefaultStartDate();
      }
  
      form.setFieldsValue({
        item_cogs: product?.item_cogs || "",
        item_cogs_batch_start_date: initialDate.isValid() ? initialDate : null,
      });
  
      setHasStartDate(initialDate.isValid());
  
      // Fetch history
      if (sku || product?.sku) {
        fetchCogsHistory();
      }
  
    } else {
      form.resetFields();
      setCogsHistory([]);
      setError(null);
      setActiveRightTab("table");
      setHasStartDate(true);
      setEditingRecord(null);
    }
  }, [visible, product]);

  const fetchCogsHistory = async () => {
    setLoadingHistory(true);
    setError(null);
    try {
      const productSku = sku || product?.sku;
      if (!productSku) return;

      const response = await MakeApiCall(
        'products/cogs-history?no_pagination=true',
        'post',
        buildCogsHistoryRequestBody(productSku, marketplaceId),
        true,
        null,
        userData?.token
      );

      const { records: rawRecords } = extractCogsHistoryRecords(response);

      if (rawRecords.length > 0) {
        const sortedRecords = [...rawRecords].sort((a, b) => a.created_at - b.created_at);

        const formattedData = sortedRecords.map(record => ({
          id: record.id,
          timestamp: record.created_at,
          dateTime: formatDateTime(record.created_at),
          dateForXAxis: formatDateForXAxis(record.created_at),
          cogs: parseFloat(record.item_cogs),
          batchStartDate: record.item_cogs_batch_start_date,
          currency: record.currency || 'INR',
          marketplace_id: record.marketplace_id
        }));

        setCogsHistory(formattedData);
      } else {
        setCogsHistory([]);
      }
    } catch (error) {
      console.error("Failed to fetch COGS history:", error);
      setError("Failed to load COGS history");
      setCogsHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Helper function to get the min and max allowed dates for editing a record
  const getDateBoundariesForEdit = (currentRecord, allHistory) => {
    // Find the index of the current record (sorted from oldest to newest)
    const currentIndex = allHistory.findIndex(r => r.id === currentRecord.id);
    
    let minDate = null;
    let maxDate = null;
    
    // For the first record (oldest)
    if (currentIndex === 0) {
      // Min date = SP API Connected Date - 60 days (if available)
      if (spApiConnectedDate && spApiConnectedDate.isValid()) {
        minDate = spApiConnectedDate.subtract(60, "day").startOf("day");
      } else {
        // If no SP API date, allow any past date
        minDate = dayjs().subtract(1, "year").startOf("day");
      }
      
      // Max date = next record's date - 1 day (if next record exists)
      if (allHistory.length > 1 && allHistory[currentIndex + 1] && allHistory[currentIndex + 1].batchStartDate) {
        const nextDate = dayjs(allHistory[currentIndex + 1].batchStartDate);
        if (nextDate.isValid()) {
          maxDate = nextDate.subtract(1, "day");
        }
      }
    } 
    // For the last record (newest)
    else if (currentIndex === allHistory.length - 1) {
      // Min date = previous record's date + 1 day
      const previousRecord = allHistory[currentIndex - 1];
      if (previousRecord && previousRecord.batchStartDate) {
        const prevDate = dayjs(previousRecord.batchStartDate);
        if (prevDate.isValid()) {
          minDate = prevDate.add(1, "day");
        }
      }
      // No max date for last record (can go to future)
      maxDate = null;
    }
    // For middle records
    else {
      // Min date = previous record's date + 1 day
      const previousRecord = allHistory[currentIndex - 1];
      if (previousRecord && previousRecord.batchStartDate) {
        const prevDate = dayjs(previousRecord.batchStartDate);
        if (prevDate.isValid()) {
          minDate = prevDate.add(1, "day");
        }
      }
      
      // Max date = next record's date - 1 day
      const nextRecord = allHistory[currentIndex + 1];
      if (nextRecord && nextRecord.batchStartDate) {
        const nextDate = dayjs(nextRecord.batchStartDate);
        if (nextDate.isValid()) {
          maxDate = nextDate.subtract(1, "day");
        }
      }
    }
    
    return { minDate, maxDate };
  };

  // Handle edit click
  const handleEditClick = (record) => {
    if (readOnly) return;
    setEditingRecord(record.id);
    setEditFormData({
      item_cogs: record.cogs,
      item_cogs_batch_start_date: record.batchStartDate && dayjs(record.batchStartDate).isValid() 
        ? dayjs(record.batchStartDate) 
        : null
    });
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingRecord(null);
    setEditFormData({ item_cogs: '', item_cogs_batch_start_date: null });
  };

  // Handle save edit
  const handleSaveEdit = async (record) => {
    if (!editFormData.item_cogs) {
      message.error("Please enter COGS value");
      return;
    }

    if (!editFormData.item_cogs_batch_start_date || !editFormData.item_cogs_batch_start_date.isValid()) {
      message.error("Please select a valid start date");
      return;
    }

    // Validate date boundaries
    const { minDate, maxDate } = getDateBoundariesForEdit(record, cogsHistory);
    
    if (minDate && editFormData.item_cogs_batch_start_date.isBefore(minDate, 'day')) {
      message.error(`Start date must be after ${minDate.format('DD-MM-YYYY')}`);
      return;
    }
    
    if (maxDate && editFormData.item_cogs_batch_start_date.isAfter(maxDate, 'day')) {
      message.error(`Start date must be before ${maxDate.format('DD-MM-YYYY')}`);
      return;
    }

    setUpdatingCogs(true);
    try {
      const payload = {
        item_cogs: parseFloat(editFormData.item_cogs),
        item_cogs_batch_start_date: dayjs(editFormData.item_cogs_batch_start_date).format('DD-MM-YYYY')
      };

      const response = await MakeApiCall(
        `products/edit-cogs-history/${record.id}`,
        'post',
        payload,
        true,
        null,
        userData?.token
      );

      if (response?.status) {
        message.success("COGS history updated successfully");
        // Refresh the history
        await fetchCogsHistory();
        // Also refresh the main product list to update current COGS if needed
        if (getList) {
          getList();
        }
        setEditingRecord(null);
      } else {
        message.error(response?.message || "Failed to update COGS history");
      }
    } catch (error) {
      console.error("Failed to update COGS history:", error);
      message.error("Something went wrong. Please try again.");
    } finally {
      setUpdatingCogs(false);
    }
  };

  const handleFormSubmit = () => {
    if (readOnly) return;
    form
      .validateFields()
      .then(async (values) => {
        setSubmitting(true);
        
        // Validate start date
        if (!values.item_cogs_batch_start_date || !values.item_cogs_batch_start_date.isValid()) {
          message.error("Please select a valid start date");
          setSubmitting(false);
          return;
        }
        
        // Get the latest COGS history date for validation
        let latestBatchStartDate = null;
        if (cogsHistory.length > 0) {
          // Find the record with most recent batch start date
          const sortedByDate = [...cogsHistory].sort((a, b) => {
            if (!a.batchStartDate) return 1;
            if (!b.batchStartDate) return -1;
            return dayjs(b.batchStartDate).diff(dayjs(a.batchStartDate));
          });
          if (sortedByDate[0] && sortedByDate[0].batchStartDate) {
            latestBatchStartDate = dayjs(sortedByDate[0].batchStartDate);
          }
        }
        
        // Validate that new start date is after the latest batch start date
        if (latestBatchStartDate && values.item_cogs_batch_start_date.isBefore(latestBatchStartDate, 'day')) {
          message.error(`Start date must be after the latest batch start date (${latestBatchStartDate.format('DD-MM-YYYY')})`);
          setSubmitting(false);
          return;
        }
        
        // Check if date is same as latest batch start date
        if (latestBatchStartDate && values.item_cogs_batch_start_date.isSame(latestBatchStartDate, 'day')) {
          message.error(`Start date cannot be the same as existing batch start date (${latestBatchStartDate.format('DD-MM-YYYY')}). Please select a later date.`);
          setSubmitting(false);
          return;
        }
        
        const formattedData = {
          item_cogs: values.item_cogs,
          item_cogs_batch_start_date: dayjs(values.item_cogs_batch_start_date).format('DD-MM-YYYY')
        };

        try {
          let response;
          if (sku) {
            // Dashboard / SKU-based mode
            const payload = {
              sku,
              item_cogs: formattedData.item_cogs,
              item_cogs_batch_start_date: formattedData.item_cogs_batch_start_date,
              ...(marketplaceId ? { marketplace_id: marketplaceId } : {}),
            };
            response = await MakeApiCall('products/update-sku-cogs', 'post', payload, true, null, userData?.token);
          } else {
            // Products-page mode
            response = await MakeApiCall(`update-product/${product?.id}`, 'post', formattedData, true, null, userData?.token);
          }

          if (response?.status) {
            message.success(response?.message);
            // Refresh form with new values
            form.setFieldsValue({
              item_cogs: values.item_cogs,
              item_cogs_batch_start_date: values.item_cogs_batch_start_date
                ? dayjs(values.item_cogs_batch_start_date)
                : null,
            });
            // Update product object with new values
            if (product) {
              product.item_cogs = values.item_cogs;
              product.item_cogs_batch_start_date = formattedData.item_cogs_batch_start_date;
            }
            getList && getList();
            // Refresh history after update
            await fetchCogsHistory();
          } else {
            message.error(response?.message);
          }
        } catch (error) {
          message.error("Something went wrong. Please try again.");
          console.error("Request Failed:", error);
        } finally {
          setSubmitting(false);
        }
      })
      .catch((errorInfo) => {
        message.error("Validation Failed");
        console.log("Validation failed:", errorInfo);
        setSubmitting(false);
      });
  };

  const handleCancel = () => {
    form.resetFields();
    onClose(false);
  };

  // Prepare chart data
  const hasData = cogsHistory.length > 0;
  const cogsColor = isDark ? '#f45b68' : '#314F71';
  // console.log(cogsHistory);
  const chartData = {
    labels: hasData ? cogsHistory.map(record => record.batchStartDate) : [],
    datasets: [
      {
        label: 'COGS (Cost per unit)',
        data: hasData ? cogsHistory.map(record => record.cogs) : [],
        borderColor: cogsColor,
        borderWidth: 2.5,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: cogsColor,
        pointBorderColor: isDark ? '#1f2937' : '#fff',
        pointBorderWidth: 1,
        fill: false,
      }
    ]
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (tooltipItems) => {
            const index = tooltipItems[0].dataIndex;
            if (cogsHistory[index] && cogsHistory[index].batchStartDate) {
              // Show Batch Start Date in Tooltip Title
              return `Batch Start Date: ${cogsHistory[index].batchStartDate}`;
            }
            return tooltipItems[0].label;
          },
  
          label: (context) => {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            const value = context.parsed.y;
            if (value === null || value === undefined) {
              label += '—';
            } else {
              label += `₹${value.toFixed(2)}`;
            }
            return label;
          },
          afterBody: (tooltipItems) => {
            const index = tooltipItems[0].dataIndex; 
            if (cogsHistory[index] && cogsHistory[index].batchStartDate) {
              // return [`Batch Start: ${cogsHistory[index].batchStartDate}`];
            }
            return [];
          }
        },
        backgroundColor: isDark ? '#1f2937' : 'white',
        titleColor: isDark ? '#e5e7eb' : '#333',
        bodyColor: isDark ? '#cbd5e1' : '#666',
        borderColor: isDark ? '#3f4254' : '#ddd',
        borderWidth: 1,
        padding: 10,
        displayColors: true,
      },
      legend: {
        position: 'top',
        align: 'center',
        labels: {
          color: isDark ? '#e5e7eb' : '#3f4254',
          font: {
            size: 11,
          },
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
          padding: 10,
        }
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: 10,
          },
          color: isDark ? '#a1a5b7' : '#888',
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: 8,
        },
        grid: {
          display: false,
        },
      },
      y: {
        position: 'left',
        ticks: {
          callback: (value) => {
            if (value === null || value === undefined) return '—';
            return `₹${value.toFixed(2)}`;
          },
          font: {
            size: 10,
          },
          color: cogsColor,
        },
        grid: {
          color: isDark ? 'rgba(161, 165, 183, 0.28)' : '#f0f0f0',
          drawBorder: false,
          lineWidth: 1,
        },
      }
    },
    elements: {
      line: {
        borderJoin: 'round',
        borderCap: 'round',
      },
      point: {
        hoverRadius: 6,
        radius: 3,
      }
    },
    layout: {
      padding: {
        top: 10,
        bottom: 10,
        left: 10,
        right: 10
      }
    }
  };

  // Helper to get absolute minimum date for main form
  const getMainFormMinDate = () => {
    // First priority: Get the latest batch start date from history
    let latestBatchStartDate = null;
    if (cogsHistory.length > 0) {
      const sortedByDate = [...cogsHistory].sort((a, b) => {
        if (!a.batchStartDate) return 1;
        if (!b.batchStartDate) return -1;
        return dayjs(b.batchStartDate).diff(dayjs(a.batchStartDate));
      });
      if (sortedByDate[0] && sortedByDate[0].batchStartDate) {
        latestBatchStartDate = dayjs(sortedByDate[0].batchStartDate);
      }
    }
    
    // If we have history, min date should be the latest batch start date + 1 day
    if (latestBatchStartDate && latestBatchStartDate.isValid()) {
      return latestBatchStartDate.add(1, "day").startOf("day");
    }
    
    // If no history, use SP API date - 60 days
    if (spApiConnectedDate && spApiConnectedDate.isValid()) {
      return spApiConnectedDate.subtract(60, "day").startOf("day");
    }
    
    // Default fallback
    return dayjs().subtract(1, "year").startOf("day");
  };

  // Right side tabs items
  const rightTabItems = [
    {
      key: 'chart',
      label: '📈 Chart View',
      children: (
        <div style={{ minHeight: 350 }}>
          {loadingHistory && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 350 }}>
              <Spin size="large" tip="Loading COGS history..." />
            </div>
          )}

          {!loadingHistory && error && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 350 }}>
              <Empty description={error} />
            </div>
          )}

          {!loadingHistory && !error && !hasData && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 350 }}>
              <Empty description="No COGS history available for this product" />
            </div>
          )}

          {!loadingHistory && !error && hasData && (
            <>
              {/* Chart */}
              <Line
                ref={chartRef}
                data={chartData}
                options={chartOptions}
                height={150}
              />
            </>
          )}
        </div>
      )
    },
    {
      key: 'table',
      label: '📋 Table View',
      children: (
        <div>
          {loadingHistory && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
              <Spin size="large" tip="Loading COGS history..." />
            </div>
          )}

          {!loadingHistory && error && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
              <Empty description={error} />
            </div>
          )}

          {!loadingHistory && !error && !hasData && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
              <Empty description="No COGS history available for this product" />
            </div>
          )}

          {!loadingHistory && !error && hasData && (
            <>
              {/* Data Table with Edit Functionality */}
              <div className="products-cogs-history-table-wrap" style={{
                maxHeight: '320px',
                overflow: 'auto',
                borderRadius: '6px'
              }}>
                <table className="table table-sm table-hover products-cogs-history-table" style={{ fontSize: '12px', marginBottom: 0 }}>
                  <thead className="products-cogs-history-thead" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ padding: '10px', width: '50px' }}>#</th>
                      {/* <th style={{ padding: '10px' }}>Date & Time</th> */}
                      <th style={{ padding: '10px', width: '120px' }}>COGS (₹)</th>
                      <th style={{ padding: '10px', width: '130px' }}>Batch Start Date</th>
                      <th style={{ padding: '10px', width: '70px' }}>Currency</th>
                      <th style={{ padding: '10px', width: '80px', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cogsHistory.slice().reverse().map((record, idx) => {
                      // Get date boundaries for this record
                      const { minDate, maxDate } = getDateBoundariesForEdit(record, cogsHistory);
                      
                      // Get the actual record index in sorted order (for displaying boundaries info)
                      const currentIndex = cogsHistory.findIndex(r => r.id === record.id);
                      const isFirst = currentIndex === 0;
                      const isLast = currentIndex === cogsHistory.length - 1;
                      
                      return (
                        <tr key={record.id}>
                          <td style={{ padding: '8px' }}>{idx + 1}</td>
                          {/* <td style={{ padding: '8px' }}>{record.dateTime}</td> */}
                          <td style={{ padding: '8px' }}>
                            {editingRecord === record.id ? (
                              <InputNumber
                                value={editFormData.item_cogs}
                                onChange={(value) => setEditFormData({ ...editFormData, item_cogs: value })}
                                min={0}
                                precision={2}
                                size="small"
                                style={{ width: '100%' }}
                                placeholder="Enter COGS"
                                formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={value => value.replace(/₹\s?|(,*)/g, '')}
                                autoFocus
                              />
                            ) : (
                              <span className="fw-semibold" style={{ color: cogsColor }}>
                                ₹{record.cogs.toFixed(2)}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '8px' }}>
                            {editingRecord === record.id ? (
                              <div>
                                <DatePicker
                                  value={editFormData.item_cogs_batch_start_date}
                                  onChange={(date) =>
                                    setEditFormData({
                                      ...editFormData,
                                      item_cogs_batch_start_date: date
                                    })
                                  }
                                  format="DD-MM-YYYY"
                                  size="small"
                                  style={{ width: '100%' }}
                                  placeholder="Select date"
                                  disabledDate={(current) => {
                                    if (!current) return false;
                                    
                                    // Disable dates before min date
                                    if (minDate && current < minDate.startOf('day')) {
                                      return true;
                                    }
                                    
                                    // Disable dates after max date
                                    if (maxDate && current > maxDate.endOf('day')) {
                                      return true;
                                    }
                                    
                                    return false;
                                  }}
                                />
                                {minDate && (
                                  <div className="products-cogs-date-hint" style={{ fontSize: '10px', marginTop: '4px' }}>
                                    Min: {minDate.format('DD-MM-YYYY')}
                                    {maxDate && ` | Max: ${maxDate.format('DD-MM-YYYY')}`}
                                    {isFirst && " (First record - can go up to next record)"}
                                    {isLast && " (Last record - can go to any future date)"}
                                    {!isFirst && !isLast && " (Must be between previous and next record)"}
                                  </div>
                                )}
                              </div>
                            ) : (
                              record.batchStartDate || '-'
                            )}
                          </td>
                          <td style={{ padding: '8px' }}>{record.currency}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            {editingRecord === record.id ? (
                              <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                <Popconfirm
                                  title="Save Changes"
                                  description="Are you sure you want to save these changes?"
                                  onConfirm={() => handleSaveEdit(record)}
                                  okText="Yes"
                                  cancelText="No"
                                  placement="topRight"
                                >
                                  <Button
                                    type="link"
                                    size="small"
                                    icon={<SaveOutlined />}
                                    loading={updatingCogs}
                                    className="products-cogs-save-btn"
                                    style={{ padding: '0 4px' }}
                                  />
                                </Popconfirm>
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<CloseOutlined />}
                                  onClick={handleCancelEdit}
                                  className="products-cogs-cancel-btn"
                                  style={{ padding: '0 4px' }}
                                />
                              </div>
                            ) : (
                              <Tooltip title={readOnly ? 'View only' : 'Edit COGS'}>
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => handleEditClick(record)}
                                  disabled={readOnly}
                                  style={{ padding: '0 4px' }}
                                />
                              </Tooltip>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Note about editing */}
              <div className="mt-2 text-muted" style={{ fontSize: '11px', fontStyle: 'italic' }}>
                <i className="ki-outline ki-information-2"></i> 
                <strong>Center Date Validation:</strong> Each record's date must be between its neighboring records:
                <ul style={{ marginTop: '4px', marginBottom: '0', paddingLeft: '20px' }}>
                  <li>First record: Between {spApiConnectedDate ? dayjs(spApiConnectedDate).subtract(60, "day").format('DD-MM-YYYY') : 'minimum date'} and next record's date</li>
                  <li>Middle records: Between previous and next record's dates</li>
                  <li>Last record: After previous record's date (future dates allowed)</li>
                </ul>
              </div>
            </>
          )}
        </div>
      )
    }
  ];

  // Handler for DatePicker's value change to update hasStartDate
  const handleStartDateChange = (date) => {
    setHasStartDate(!!date && date.isValid());
    form.setFieldsValue({ item_cogs_batch_start_date: date });
  };

  return (
    <Modal
      className="products-cogs-modal"
      title={
        <div className="d-flex flex-column gap-1">
          <span className="fw-bold fs-4 mb-1 products-cogs-modal-title">Update COGS</span>
          <div className="fs-7 d-flex flex-wrap gap-2 ">
            <div className="w-75 text-one"><b>Product:</b> {product?.item_name || product?.product_title || product?.name}</div>
            <div className="d-flex gap-2 w-100">
                <span className="ms-0"><b>SKU:</b> {sku || product?.sku}</span>
                {marketplaceId && <span className="ms-2"><b>Marketplace:</b> {marketplaceId}</span>}
            </div>
          </div>
        </div>
      }
      open={visible}
      width={1200}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
      centered
      bodyStyle={{ padding: '20px' }}
    >
      <Wrapper>
        <div className="row">
          {/* Left Side - Update COGS Section */}
          <div className="col-md-5 products-cogs-left-panel" style={{ paddingRight: '24px' }}>
            <div className="d-flex justify-content-between align-items-center mb-4 min-h-40px">
              <h5 className="products-cogs-section-title" style={{ fontWeight: 600, margin: 0 }}>Update COGS</h5>
            </div>

            <Form form={form} onFinish={handleFormSubmit} layout="vertical">
              <div className="mb-4">
                <label className="fs-6 fw-bold mb-2">
                  Cost per unit <span className="products-cogs-required">*</span>
                </label>
                <Form.Item
                  name="item_cogs"
                  className='mb-0'
                  rules={[{ required: true, message: 'Please enter Cost' }]}
                >
                  <InputNumber
                    className="w-100"
                    variant="filled"
                    size='large'
                    min={0}
                    precision={2}
                    disabled={readOnly}
                    placeholder="Enter cost per unit"
                    formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/₹\s?|(,*)/g, '')}
                    onKeyDown={(e) => {
                      const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter', '.'];
                      if (allowed.includes(e.key)) return;
                      if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x', 'z'].includes(e.key.toLowerCase())) return;
                      if (e.key < '0' || e.key > '9') { e.preventDefault(); }
                    }}
                  />
                </Form.Item>
              </div>
              
              <div className="mb-4">
                <label className="fs-6 fw-bold mb-2">Start Date <span className="products-cogs-required">*</span></label>
                <Form.Item
                  name="item_cogs_batch_start_date"
                  className='mb-0'
                  rules={[{ required: true, message: 'Please select Start Date' }]}
                >
                  <DatePicker
                    className="w-100"
                    variant="filled"
                    size='large'
                    format="DD-MM-YYYY"
                    placeholder="Select Start Date"
                    style={{ width: '100%' }}
                    allowClear={true}
                    disabled={readOnly}
                    onChange={handleStartDateChange}
                    disabledDate={(current) => {
                      if (!current) return false;
                      const minDate = getMainFormMinDate();
                      // Disable dates before minimum date
                      if (minDate && current < minDate.startOf('day')) {
                        return true;
                      }
                      return false;
                    }}
                  />
                </Form.Item>
                <small className="text-muted">
                  Date from which this COGS becomes effective. 
                  {cogsHistory.length > 0 && ` Must be after ${dayjs(getMainFormMinDate()).subtract(1, 'day').format('DD-MM-YYYY')}`}
                </small>
              </div>
              
              {/* Current COGS Info */}
              {(product?.item_cogs !== undefined && product?.item_cogs !== null && Number(product.item_cogs) !== 0) && (
                <div className="mt-4 p-3 products-cogs-current-card" style={{ borderRadius: '8px' }}>
                  <div className="text-muted mb-1">Current COGS</div>
                  <div className="fw-bold fs-4 products-cogs-current-value">
                    ₹{parseFloat(product.item_cogs).toFixed(2)}
                  </div>
                  {product?.item_cogs_batch_start_date && (
                    <div className="text-muted small mt-1">
                      Effective from: {product.item_cogs_batch_start_date}
                    </div>
                  )}
                </div>
              )}
         
              {/* Action Buttons */}
              <div className="mt-4 d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-light products-cogs-modal-cancel-btn"
                  onClick={handleCancel}
                  style={{ padding: '8px 20px', borderRadius: '6px' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => form.submit()}
                  disabled={readOnly || submitting || !hasStartDate}
                  style={{ padding: '8px 20px', borderRadius: '6px' }}
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </Form>
          </div>

          {/* Right Side - History Section with Tabs */}
          <div className="col-md-7" style={{ paddingLeft: '24px' }}>
            <div className="d-flex justify-content-between align-items-center mb-3 ">
              <h5 className="products-cogs-section-title" style={{ fontWeight: 600, margin: 0 }}>
                COGS History
              </h5>
              <Segmented
                size="middle"
                options={[
                    {
                        label: <span>Table View</span>,
                        value: 'table'
                    },
                    {
                        label: <span>Chart View</span>,
                        value: 'chart'
                    }
                ]}
                value={activeRightTab}
                onChange={setActiveRightTab}
                style={{ marginBottom: 0 }}
              />
            </div>
            
            <div style={{ marginTop: '8px' }}>
              {rightTabItems.find(tab => tab.key === activeRightTab)?.children}
            </div>
          </div>
        </div>
      </Wrapper>
    </Modal>
  );
};

export default AddCogs;